import { useEffect, useMemo, useRef, useState } from "react";
import PomodoroTimer from "./components/PomodoroTimer";
import TodoList from "./components/TodoList";
import Stats from "./components/Stats";
import Modal from "./components/Modal";
import SettingsPanel from "./components/SettingsPanel";
import { useLocalStorage } from "./hooks/useLocalStorage";
import type { Settings, Stats as StatsType, Todo } from "./types";
import { STORAGE_KEYS, todayKey } from "./utils/storage";

const DEFAULT_SETTINGS: Settings = {
  focus: 25,
  shortBreak: 5,
  longBreak: 15,
  autoStart: false,
  longBreakInterval: 4
};

const DEFAULT_STATS: StatsType = {
  totalPomodoros: 0,
  totalMinutes: 0,
  byDate: {}
};

export default function App() {
  const [settings, setSettings] = useLocalStorage<Settings>(
    STORAGE_KEYS.settings,
    DEFAULT_SETTINGS
  );
  const [todos, setTodos] = useLocalStorage<Todo[]>(STORAGE_KEYS.todos, []);
  const [stats, setStats] = useLocalStorage<StatsType>(STORAGE_KEYS.stats, DEFAULT_STATS);
  const [activeTodoId, setActiveTodoId] = useLocalStorage<string | null>(
    STORAGE_KEYS.activeTodoId,
    null
  );
  const [completedInRound, setCompletedInRound] = useLocalStorage<number>(
    STORAGE_KEYS.completedInRound,
    0
  );

  const [showStats, setShowStats] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  // 触发震动效果（点击"请先选择任务"时滚动 + 高亮）
  const [taskShake, setTaskShake] = useState(0);
  const taskAnchorRef = useRef<HTMLDivElement | null>(null);

  const activeTodo = useMemo(
    () => todos.find((t) => t.id === activeTodoId && !t.completed) ?? null,
    [todos, activeTodoId]
  );

  useEffect(() => {
    if (activeTodoId && !activeTodo) setActiveTodoId(null);
  }, [activeTodoId, activeTodo, setActiveTodoId]);

  function bumpActiveTodoPomodoro() {
    if (!activeTodoId) return;
    setTodos((prev) =>
      prev.map((t) =>
        t.id === activeTodoId ? { ...t, completedPomodoros: t.completedPomodoros + 1 } : t
      )
    );
  }

  function scrollToTasks() {
    taskAnchorRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    setTaskShake((x) => x + 1);
  }

  const todayDone = stats.byDate[todayKey()]?.pomodoros ?? 0;
  const activeCount = todos.filter((t) => !t.completed).length;

  return (
    <div className="min-h-full flex items-start sm:items-center justify-center p-4 sm:p-8">
      <div className="w-full max-w-3xl">
        <div className="bg-paper border border-hairline rounded-2xl shadow-card overflow-hidden">
          {/* Header */}
          <header className="px-6 py-4 border-b border-hairline flex items-center justify-between bg-paper">
            <div className="flex items-center gap-3">
              <div className="relative w-9 h-9 rounded-xl bg-focus flex items-center justify-center shadow-insetBtn">
                <span className="text-white font-numeric text-base font-semibold">P</span>
                <span className="absolute -bottom-1 -right-1 w-3 h-3 rounded-full bg-amber border-2 border-paper" />
              </div>
              <div>
                <h1 className="text-base font-semibold text-ink leading-tight">番茄钟</h1>
                <p className="text-[11px] text-soft tracking-wider uppercase">
                  Focus · Plan · Review
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {/* 今日完成 chip */}
              <button
                onClick={() => setShowStats(true)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border ${
                  todayDone > 0
                    ? "border-transparent bg-focus text-white shadow-insetBtn"
                    : "border-line bg-paper text-muted hover:bg-chip hover:text-ink"
                }`}
                title="查看统计"
              >
                <span>今日</span>
                <span className="font-numeric font-semibold tabular-nums">{todayDone}</span>
              </button>

              {/* 统计按钮 */}
              <button
                onClick={() => setShowStats(true)}
                className="w-8 h-8 rounded-md text-muted hover:text-ink hover:bg-chip flex items-center justify-center"
                title="统计"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M3 21h18M5 21V9m4 12V5m4 16v-9m4 9v-6"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                  />
                </svg>
              </button>

              {/* 设置按钮 */}
              <button
                onClick={() => setShowSettings(true)}
                className="w-8 h-8 rounded-md text-muted hover:text-ink hover:bg-chip flex items-center justify-center"
                title="设置"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M12 15a3 3 0 100-6 3 3 0 000 6z"
                    stroke="currentColor"
                    strokeWidth="1.8"
                  />
                  <path
                    d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 11-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 11-4 0v-.09a1.65 1.65 0 00-1-1.51 1.65 1.65 0 00-1.82.33l-.06.06A2 2 0 113.27 16.9l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H2a2 2 0 110-4h.09a1.65 1.65 0 001.51-1 1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 112.83-2.83l.06.06a1.65 1.65 0 001.82.33H8a1.65 1.65 0 001-1.51V3a2 2 0 114 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 112.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 110 4h-.09a1.65 1.65 0 00-1.51 1z"
                    stroke="currentColor"
                    strokeWidth="1.5"
                  />
                </svg>
              </button>
            </div>
          </header>

          {/* 主区域：倒计时 + 任务列表（单页） */}
          <main className="p-6 sm:p-10 bg-page flex flex-col gap-10">
            <PomodoroTimer
              settings={settings}
              setStats={setStats}
              activeTodo={activeTodo}
              bumpActiveTodoPomodoro={bumpActiveTodoPomodoro}
              completedInRound={completedInRound}
              setCompletedInRound={setCompletedInRound}
              onRequestPickTask={scrollToTasks}
            />

            {/* 任务区 */}
            <section
              ref={taskAnchorRef}
              key={taskShake}
              className={`bg-paper border border-hairline rounded-2xl shadow-card p-5 ${taskShake > 0 ? "animate-[shake_0.4s_ease-in-out]" : ""}`}
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-sm font-semibold text-ink flex items-center gap-2">
                  <span className="w-1.5 h-4 bg-short rounded-full" />
                  任务列表
                  <span className="text-xs text-soft font-normal tabular-nums">
                    · {activeCount} 项进行中
                  </span>
                </h2>
                {activeTodo && (
                  <span className="inline-flex items-center gap-1.5 text-xs text-focus bg-focus-soft px-2 py-1 rounded-full border border-hairline">
                    <span className="w-1.5 h-1.5 rounded-full bg-focus animate-pulse" />
                    已选：{activeTodo.title}
                  </span>
                )}
              </div>
              <TodoList
                todos={todos}
                setTodos={setTodos}
                activeTodoId={activeTodoId}
                setActiveTodoId={setActiveTodoId}
              />
            </section>
          </main>
        </div>

        <p className="text-center text-xs text-soft mt-5 tracking-wider">
          所有数据保存在本地浏览器 · 关闭后不会丢失
        </p>
      </div>

      {/* 统计 Modal */}
      <Modal
        open={showStats}
        onClose={() => setShowStats(false)}
        title="番茄统计"
        subtitle={`累计完成 ${stats.totalPomodoros} 个番茄 · 共 ${Math.round(stats.totalMinutes / 60)} 小时`}
      >
        <Stats stats={stats} todos={todos} />
      </Modal>

      {/* 设置 Modal */}
      <Modal
        open={showSettings}
        onClose={() => setShowSettings(false)}
        title="设置"
        subtitle="调整时长与节奏"
        maxWidth="max-w-lg"
      >
        <SettingsPanel
          settings={settings}
          setSettings={setSettings}
          onReset={() => {
            if (window.confirm("确认重置所有统计数据？此操作不可撤销。")) {
              setStats(DEFAULT_STATS);
              setCompletedInRound(0);
            }
          }}
        />
      </Modal>

      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-6px); }
          75% { transform: translateX(6px); }
        }
      `}</style>
    </div>
  );
}
